/**
 * Image Generator Custom Horizontal Scrollbar
 *
 * Renders a draggable scrollbar at the bottom of the panels container.
 * Supports both mouse and touch drag interactions.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface ImageGeneratorScrollbarProps {
  /** Ref to the scrollable panels container */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Array of minimized instances, used to trigger re-measurement on change */
  minimizedInstances: boolean[];
}

interface ScrollState {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

export function ImageGeneratorScrollbar({
  containerRef,
  minimizedInstances,
}: ImageGeneratorScrollbarProps) {
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<ScrollState>({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  // Track horizontal scroll position
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      setScrollState({
        scrollLeft: container.scrollLeft,
        scrollWidth: container.scrollWidth,
        clientWidth: container.clientWidth,
      });
    };

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [containerRef, minimizedInstances]);

  // Scrollbar drag handling
  const handleScrollbarDrag = useCallback(
    (clientX: number) => {
      const container = containerRef.current;
      const track = scrollTrackRef.current;
      if (!container || !track) return;

      const trackRect = track.getBoundingClientRect();
      const trackWidth = trackRect.width;
      const thumbWidth = 24; // w-6 = 24px
      const maxThumbLeft = trackWidth - thumbWidth;

      const relativeX = clientX - trackRect.left - thumbWidth / 2;
      const clampedX = Math.max(0, Math.min(relativeX, maxThumbLeft));

      const scrollRatio = clampedX / maxThumbLeft;
      const maxScrollLeft = scrollState.scrollWidth - scrollState.clientWidth;
      container.scrollLeft = scrollRatio * maxScrollLeft;
    },
    [containerRef, scrollState],
  );

  // Only render when content overflows
  if (scrollState.scrollWidth <= scrollState.clientWidth) {
    return null;
  }

  const thumbPosition =
    scrollState.scrollWidth > scrollState.clientWidth
      ? (scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth)) * 100
      : 0;
  const thumbOffset =
    scrollState.scrollWidth > scrollState.clientWidth
      ? (scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth)) * 24
      : 0;

  return (
    <div className="flex-shrink-0 border-t border-border bg-muted/30 px-12 py-3">
      <div
        ref={scrollTrackRef}
        className="relative h-6 cursor-pointer flex items-center"
        onMouseDown={(e) => {
          e.preventDefault();
          handleScrollbarDrag(e.clientX);

          const handleMouseMove = (moveEvent: MouseEvent) => {
            handleScrollbarDrag(moveEvent.clientX);
          };
          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };
          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          const touch = e.touches[0];
          handleScrollbarDrag(touch.clientX);

          const handleTouchMove = (moveEvent: TouchEvent) => {
            const moveTouch = moveEvent.touches[0];
            handleScrollbarDrag(moveTouch.clientX);
          };
          const handleTouchEnd = () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
          };
          document.addEventListener('touchmove', handleTouchMove, { passive: false });
          document.addEventListener('touchend', handleTouchEnd);
        }}
      >
        {/* Thin track line */}
        <div className="absolute inset-x-0 h-1 bg-muted rounded-full" />
        {/* Circular Thumb */}
        <div
          className="absolute w-6 h-6 bg-primary/80 hover:bg-primary rounded-full transition-colors shadow-sm"
          style={{
            left: `calc(${thumbPosition}% - ${thumbOffset}px)`,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      </div>
    </div>
  );
}
